import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import heroImage from '../assets/images/misc/hero.jpg';
import { getPublicNotices, getNoticeFileUrl, type ImportantNotice } from '../api/notices';
import { withTimeout } from '../shared/lib/withTimeout';
import {
  getPublicEvents,
  formatEventDate,
  formatEventTime,
  type Event,
} from '../features/events';
import {
  getPublicProjects,
  getProjectProgress,
  formatCurrency,
  type Project,
} from '../features/projects';
import { getImageUrlById } from '../features/images';
import { getTodaySalaahTimes, type SalaahTimings } from '../features/salaahTimes';
import { getEventShareUrl, getNoticeShareUrl, getProjectShareUrl } from '../shared/lib';
import ContactModal from './ContactModal';
import '../shared/ui/DetailLightbox.css';

const showEventsAndProjects = process.env.REACT_APP_SHOW_EVENTS_AND_PROJECTS !== 'false';

const ROTATION_INTERVAL_MS = 10000;
const PUBLIC_NOTICES_TIMEOUT_MS = 2500;
const PUBLIC_EVENTS_PROJECTS_TIMEOUT_MS = 2500;

type HeroTab = 'notices' | 'events' | 'projects';

const formatPostedDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const HeroEmptyState: React.FC<{
  icon: string;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  actionTo?: string;
}> = ({ icon, title, message, actionLabel, onAction, actionTo }) => (
  <div className="hero-empty-state text-center py-4 px-2">
    <div className="hero-empty-state__icon" aria-hidden>
      <i className={`bi ${icon}`} />
    </div>
    <h5 className="hero-empty-state__title">{title}</h5>
    <p className="hero-empty-state__message mb-3">{message}</p>
    {actionLabel && actionTo ? (
      <Link to={actionTo} className="btn btn-sm hero-empty-state__action">
        {actionLabel}
      </Link>
    ) : null}
    {actionLabel && onAction && !actionTo ? (
      <button type="button" className="btn btn-sm hero-empty-state__action" onClick={onAction}>
        {actionLabel}
      </button>
    ) : null}
  </div>
);

const HeroPanelSkeleton: React.FC = () => (
  <div className="hero-panel-skeleton" aria-hidden>
    <div className="hero-panel-skeleton__media" />
    <div className="hero-panel-skeleton__line hero-panel-skeleton__line--lg" />
    <div className="hero-panel-skeleton__line" />
  </div>
);

const SALAAH_ORDER: Array<{ key: keyof SalaahTimings; label: string }> = [
  { key: 'fajr', label: 'Fajr' },
  { key: 'dhuhr', label: 'Dhuhr' },
  { key: 'asr', label: 'Asr' },
  { key: 'maghrib', label: 'Maghrib' },
  { key: 'isha', label: 'Isha' },
];

function getNextSalaah(timings: SalaahTimings): { label: string; time: string } {
  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  for (const prayer of SALAAH_ORDER) {
    const [h, m] = timings[prayer.key].split(':').map(Number);
    if (Number.isFinite(h) && Number.isFinite(m) && h * 60 + m > current) {
      return { label: prayer.label, time: timings[prayer.key] };
    }
  }
  return { label: 'Fajr', time: timings.fajr };
}

const HeroMediaStage: React.FC<{
  src?: string;
  alt: string;
  fallbackIcon: string;
  fallbackLabel: string;
  openLabel: string;
  onOpen: () => void;
  count: number;
  itemLabel: string;
  onPrev: () => void;
  onNext: () => void;
}> = ({ src, alt, fallbackIcon, fallbackLabel, openLabel, onOpen, count, itemLabel, onPrev, onNext }) => (
  <div className="hero-stage">
    <button type="button" className="hero-stage__frame" onClick={onOpen} aria-label={openLabel}>
      {src ? (
        <>
          <span className="hero-stage__backdrop" style={{ backgroundImage: `url(${src})` }} aria-hidden />
          <img src={src} alt={alt} className="hero-stage__image" />
        </>
      ) : (
        <span className="hero-media-fallback hero-stage__fallback">
          <i className={`bi ${fallbackIcon}`} aria-hidden />
          <span>{fallbackLabel}</span>
        </span>
      )}
      <span className="hero-stage__expand" aria-hidden>
        <i className="bi bi-arrows-angle-expand" />
      </span>
    </button>
    {count > 1 ? (
      <>
        <button
          type="button"
          className="hero-stage__nav hero-stage__nav--prev"
          onClick={onPrev}
          aria-label={`Previous ${itemLabel}`}
        >
          <i className="bi bi-chevron-left" />
        </button>
        <button
          type="button"
          className="hero-stage__nav hero-stage__nav--next"
          onClick={onNext}
          aria-label={`Next ${itemLabel}`}
        >
          <i className="bi bi-chevron-right" />
        </button>
      </>
    ) : null}
  </div>
);

const HeroSlideBar: React.FC<{
  title: string;
  meta?: React.ReactNode;
  index: number;
  count: number;
  itemLabel: string;
  onDot: (index: number) => void;
  isCopied: boolean;
  onShare: () => void;
  children?: React.ReactNode;
}> = ({ title, meta, index, count, itemLabel, onDot, isCopied, onShare, children }) => (
  <div className="hero-slidebar">
    <div className="hero-slidebar__text">
      <h3 className="hero-slidebar__title">{title}</h3>
      {meta ? <p className="hero-slidebar__meta">{meta}</p> : null}
      {children}
    </div>
    <div className="hero-slidebar__actions">
      {count > 1 ? (
        <div className="hero-slidebar__dots" role="group" aria-label={`Choose ${itemLabel}`}>
          {Array.from({ length: count }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`hero-slidebar__dot${i === index ? ' is-active' : ''}`}
              onClick={() => onDot(i)}
              aria-label={`Go to ${itemLabel} ${i + 1}`}
              aria-current={i === index}
            />
          ))}
          <span className="hero-slidebar__count">
            {index + 1}/{count}
          </span>
        </div>
      ) : null}
      <button
        type="button"
        className="hero-slidebar__share"
        onClick={onShare}
        aria-label={isCopied ? 'Link copied' : `Share ${itemLabel}`}
        title={isCopied ? 'Link copied' : 'Copy share link'}
      >
        <i className={`bi ${isCopied ? 'bi-check2' : 'bi-share'}`} aria-hidden />
      </button>
    </div>
  </div>
);

const Hero: React.FC = () => {
  const [notices, setNotices] = useState<ImportantNotice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  const [timerKey, setTimerKey] = useState(0);
  const noticesLengthRef = useRef(0);
  const eventsLengthRef = useRef(0);
  const projectsLengthRef = useRef(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<HeroTab>('notices');
  const [showContactModal, setShowContactModal] = useState(false);
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);
  const [copiedProjectId, setCopiedProjectId] = useState<string | null>(null);
  const [copiedNoticeId, setCopiedNoticeId] = useState<string | null>(null);

  const [events, setEvents] = useState<Event[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingEventsProjects, setIsLoadingEventsProjects] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);

  // Preview modals (event / project) + zoomed image
  const [previewEvent, setPreviewEvent] = useState<Event | null>(null);
  const [previewProject, setPreviewProject] = useState<Project | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [zoomedImageTitle, setZoomedImageTitle] = useState<string>('');

  // Fullscreen state for notices
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const [salaahTimings, setSalaahTimings] = useState<SalaahTimings | null>(null);
  const nextSalaah = salaahTimings ? getNextSalaah(salaahTimings) : null;

  useEffect(() => {
    getTodaySalaahTimes()
      .then((data) => setSalaahTimings(data.timings))
      .catch(() => setSalaahTimings(null));
  }, []);

  useEffect(() => {
    noticesLengthRef.current = notices.length;
  }, [notices.length]);

  const upcomingEvents = useMemo<Event[]>(() => {
    if (!events.length) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return events
      .filter((e) => {
        const d = new Date(e.dtDateOfEvent);
        d.setHours(0, 0, 0, 0);
        return d.getTime() >= today.getTime();
      })
      .sort((a, b) => new Date(a.dtDateOfEvent).getTime() - new Date(b.dtDateOfEvent).getTime());
  }, [events]);

  const activeProjects = useMemo<Project[]>(() => {
    return projects.filter((p) => {
      if (p.isCompleted) return false;
      const name = (p.name ?? '').trim();
      if (name.length < 3) return false;
      // Hide unfinished stubs (e.g. short gibberish names with no media or funding goal)
      const looksLikeStub =
        !p.imageId &&
        (p.fundsNeeded ?? 0) <= 0 &&
        (p.fundsRaised ?? 0) <= 0 &&
        name.length <= 8 &&
        !/\s/.test(name);
      return !looksLikeStub;
    });
  }, [projects]);

  useEffect(() => {
    eventsLengthRef.current = upcomingEvents.length;
  }, [upcomingEvents.length]);

  useEffect(() => {
    projectsLengthRef.current = activeProjects.length;
  }, [activeProjects.length]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const list = await withTimeout(
          getPublicNotices(),
          PUBLIC_NOTICES_TIMEOUT_MS,
          'Public notices load timeout'
        );
        setNotices(list);
      } catch (error) {
        console.warn('Public notices skipped on home page', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!showEventsAndProjects) return;
    let cancelled = false;
    setIsLoadingEventsProjects(true);
    withTimeout(
      Promise.all([getPublicEvents(), getPublicProjects()]),
      PUBLIC_EVENTS_PROJECTS_TIMEOUT_MS,
      'Public events/projects load timeout'
    )
      .then(([eventsList, projectsList]) => {
        if (cancelled) return;
        setEvents(eventsList);
        setProjects(projectsList);
      })
      .catch((err) => {
        if (!cancelled) {
          // Keep this silent on the public home hero; sections below may still succeed.
          console.warn('Public hero events/projects skipped', err);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEventsProjects(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCurrentNoticeIndex(0);
  }, [notices.length]);

  useEffect(() => {
    setCurrentEventIndex(0);
  }, [upcomingEvents.length]);

  useEffect(() => {
    setCurrentProjectIndex(0);
  }, [activeProjects.length]);

  // Reset to first notice and restart timer when user returns to this tab
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCurrentNoticeIndex(0);
        setCurrentEventIndex(0);
        setCurrentProjectIndex(0);
        setTimerKey((k) => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Auto-rotate notices
  useEffect(() => {
    if (notices.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentNoticeIndex((prev) => {
        const len = noticesLengthRef.current;
        if (len <= 1) return 0;
        return prev >= len - 1 ? 0 : prev + 1;
      });
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [notices.length, timerKey]);

  // Auto-rotate upcoming events (only while tab is active)
  useEffect(() => {
    if (activeTab !== 'events') return;
    if (upcomingEvents.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentEventIndex((prev) => {
        const len = eventsLengthRef.current;
        if (len <= 1) return 0;
        return prev >= len - 1 ? 0 : prev + 1;
      });
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeTab, upcomingEvents.length, timerKey]);

  // Auto-rotate active projects (only while tab is active)
  useEffect(() => {
    if (activeTab !== 'projects') return;
    if (activeProjects.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentProjectIndex((prev) => {
        const len = projectsLengthRef.current;
        if (len <= 1) return 0;
        return prev >= len - 1 ? 0 : prev + 1;
      });
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeTab, activeProjects.length, timerKey]);

  // Keyboard navigation for fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (zoomedImage) {
          setZoomedImage(null);
          return;
        }
        setIsFullscreen(false);
      } else if (e.key === 'ArrowLeft') {
        if (zoomedImage) return;
        setFullscreenIndex((prev) => (prev === 0 ? notices.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        if (zoomedImage) return;
        setFullscreenIndex((prev) => (prev + 1) % notices.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, notices.length, zoomedImage]);

  // Close event/project detail lightbox on Escape
  useEffect(() => {
    if (!previewEvent && !previewProject) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (zoomedImage) {
        setZoomedImage(null);
        return;
      }
      setPreviewEvent(null);
      setPreviewProject(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewEvent, previewProject, zoomedImage]);

  // Lock page scroll while any detail lightbox is open
  useEffect(() => {
    const open = Boolean(isFullscreen || previewEvent || previewProject || zoomedImage);
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isFullscreen, previewEvent, previewProject, zoomedImage]);

  const restartTimer = () => setTimerKey((k) => k + 1);

  const nextNotice = useCallback(() => {
    setCurrentNoticeIndex((prev) => (prev === notices.length - 1 ? 0 : prev + 1));
    restartTimer();
  }, [notices.length]);

  const prevNotice = useCallback(() => {
    setCurrentNoticeIndex((prev) => (prev === 0 ? notices.length - 1 : prev - 1));
    restartTimer();
  }, [notices.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentNoticeIndex(index);
    restartTimer();
  }, []);

  const nextEvent = useCallback(() => {
    setCurrentEventIndex((prev) => (prev >= upcomingEvents.length - 1 ? 0 : prev + 1));
    restartTimer();
  }, [upcomingEvents.length]);

  const prevEvent = useCallback(() => {
    setCurrentEventIndex((prev) => (prev === 0 ? Math.max(0, upcomingEvents.length - 1) : prev - 1));
    restartTimer();
  }, [upcomingEvents.length]);

  const goToEventSlide = useCallback((index: number) => {
    setCurrentEventIndex(index);
    restartTimer();
  }, []);

  const nextProject = useCallback(() => {
    setCurrentProjectIndex((prev) => (prev >= activeProjects.length - 1 ? 0 : prev + 1));
    restartTimer();
  }, [activeProjects.length]);

  const prevProject = useCallback(() => {
    setCurrentProjectIndex((prev) => (prev === 0 ? Math.max(0, activeProjects.length - 1) : prev - 1));
    restartTimer();
  }, [activeProjects.length]);

  const goToProjectSlide = useCallback((index: number) => {
    setCurrentProjectIndex(index);
    restartTimer();
  }, []);

  const openFullscreen = useCallback((index: number) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const goFullscreenNext = useCallback(() => {
    setFullscreenIndex((prev) => (prev + 1) % notices.length);
  }, [notices.length]);

  const goFullscreenPrev = useCallback(() => {
    setFullscreenIndex((prev) => (prev === 0 ? notices.length - 1 : prev - 1));
  }, [notices.length]);

  const currentNotice = notices[currentNoticeIndex];
  const fullscreenNotice = notices[fullscreenIndex];

  const currentUpcomingEvent = upcomingEvents[currentEventIndex] ?? null;
  const currentProject = activeProjects[currentProjectIndex] ?? null;

  const copyEventShareLink = useCallback(async (eventId: string) => {
    const url = getEventShareUrl(eventId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedEventId(eventId);
      setTimeout(() => setCopiedEventId((prev) => (prev === eventId ? null : prev)), 1800);
      return;
    } catch {
      // fallback below
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedEventId(eventId);
      setTimeout(() => setCopiedEventId((prev) => (prev === eventId ? null : prev)), 1800);
    } catch {
      // ignore
    }
  }, []);

  const copyProjectShareLink = useCallback(async (projectId: string) => {
    const url = getProjectShareUrl(projectId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedProjectId(projectId);
      setTimeout(() => setCopiedProjectId((prev) => (prev === projectId ? null : prev)), 1800);
      return;
    } catch {
      // fallback below
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedProjectId(projectId);
      setTimeout(() => setCopiedProjectId((prev) => (prev === projectId ? null : prev)), 1800);
    } catch {
      // ignore
    }
  }, []);

  const copyNoticeShareLink = useCallback(async (noticeId: string) => {
    const url = getNoticeShareUrl(noticeId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedNoticeId(noticeId);
      setTimeout(() => setCopiedNoticeId((prev) => (prev === noticeId ? null : prev)), 1800);
      return;
    } catch {
      // fallback below
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedNoticeId(noticeId);
      setTimeout(() => setCopiedNoticeId((prev) => (prev === noticeId ? null : prev)), 1800);
    } catch {
      // ignore
    }
  }, []);

  /** Open fullscreen from URL ?noticePreview=id (used by /share/notices/:id redirect) */
  useEffect(() => {
    const noticeId = searchParams.get('noticePreview');
    if (!noticeId || notices.length === 0) return;
    const idx = notices.findIndex((n) => n.id === noticeId);
    if (idx >= 0) {
      setActiveTab('notices');
      openFullscreen(idx);
      setSearchParams({}, { replace: true });
    }
  }, [notices, openFullscreen, searchParams, setSearchParams]);

  /** Open event preview from URL ?eventPreview=id (used by /share/events/:id redirect) */
  useEffect(() => {
    const eventId = searchParams.get('eventPreview');
    if (!eventId || events.length === 0) return;
    const event = events.find((e) => e.id === eventId);
    if (event) {
      setActiveTab('events');
      setPreviewEvent(event);
      setSearchParams({}, { replace: true });
    }
  }, [events, searchParams, setSearchParams]);

  /** Open project preview from URL ?projectPreview=id (used by /share/projects/:id redirect) */
  useEffect(() => {
    const projectId = searchParams.get('projectPreview');
    if (!projectId || projects.length === 0) return;
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      setActiveTab('projects');
      setPreviewProject(project);
      setSearchParams({}, { replace: true });
    }
  }, [projects, searchParams, setSearchParams]);

  return (
    <section
      className="py-5 hero-section hero-section--with-bg"
      style={{ backgroundImage: `url(${heroImage})` }}
    >
      <div className="hero-section__veil" aria-hidden />
      <div className="container px-3 px-sm-4 hero-section__content">
        <div className="hero-unified-card">
          <div className="row g-4 align-items-stretch">
            <div className="col-12 col-lg-5 d-flex flex-column justify-content-center">
              <span className="hero-badge mb-3">Unity · Learning · Faith</span>
              <h2 className="display-6 fw-bold hero-heading mb-3">
                Connecting &amp; Serving the Bothasig Muslim Community
              </h2>
              <p className="lead mb-4 hero-lead">
                Fostering spiritual growth, education, and unity across Bothasig and Milnerton.
              </p>

              <div className="d-flex flex-wrap gap-2 gap-sm-3 hero-cta">
                {showEventsAndProjects ? (
                  <button
                    type="button"
                    className="btn hero-cta__primary"
                    onClick={() => {
                      setActiveTab('events');
                      restartTimer();
                    }}
                  >
                    <i className="bi bi-calendar-event me-2" aria-hidden />
                    Explore Events
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn hero-cta__primary"
                    onClick={() => setShowContactModal(true)}
                  >
                    <i className="bi bi-chat-dots me-2" aria-hidden />
                    Contact us
                  </button>
                )}
                {showEventsAndProjects ? (
                  <button
                    type="button"
                    className="btn hero-cta__secondary"
                    onClick={() => setShowContactModal(true)}
                  >
                    <i className="bi bi-chat-dots me-2" aria-hidden />
                    Contact us
                  </button>
                ) : null}
                {showEventsAndProjects ? (
                  <button
                    type="button"
                    className="btn hero-cta__ghost"
                    onClick={() => {
                      setActiveTab('projects');
                      restartTimer();
                    }}
                  >
                    Support Projects
                    <i className="bi bi-arrow-right ms-2" aria-hidden />
                  </button>
                ) : null}
              </div>

              {nextSalaah ? (
                <a
                  href="#salaah"
                  className="hero-next-salaah"
                  onClick={(e) => {
                    const target = document.getElementById('salaah');
                    if (!target) return;
                    e.preventDefault();
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <span className="hero-next-salaah__icon" aria-hidden>
                    <i className="bi bi-moon-stars-fill" />
                  </span>
                  <span className="hero-next-salaah__text">
                    <span className="hero-next-salaah__label">Next salaah</span>
                    <span className="hero-next-salaah__value">
                      {nextSalaah.label} <span aria-hidden>·</span> {nextSalaah.time}
                    </span>
                  </span>
                  <span className="hero-next-salaah__link">
                    All times <i className="bi bi-chevron-down" aria-hidden />
                  </span>
                </a>
              ) : null}
            </div>

            <div className="col-12 col-lg-7">
              <div className="hero-unified-card__panel">
                <div className="hero-panel-header">
                  <h4 className="hero-events-card-title mb-0">
                    <i className={activeTab === 'notices' ? 'bi bi-megaphone-fill' : activeTab === 'events' ? 'bi bi-calendar-event' : 'bi bi-folder'} />
                    {activeTab === 'notices'
                      ? 'Important Notices'
                      : activeTab === 'events'
                        ? 'Upcoming Event'
                        : 'Active Project'}
                  </h4>

                  {showEventsAndProjects ? (
                    <div className="hero-tabs" role="tablist" aria-label="Hero content tabs">
                      <button
                        type="button"
                        id="hero-tab-notices"
                        role="tab"
                        className={`hero-tab ${activeTab === 'notices' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notices')}
                        aria-selected={activeTab === 'notices'}
                        aria-controls="hero-panel-notices"
                      >
                        Notices
                      </button>
                      <button
                        type="button"
                        id="hero-tab-events"
                        role="tab"
                        className={`hero-tab ${activeTab === 'events' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('events');
                          restartTimer();
                        }}
                        aria-selected={activeTab === 'events'}
                        aria-controls="hero-panel-events"
                      >
                        Events
                      </button>
                      <button
                        type="button"
                        id="hero-tab-projects"
                        role="tab"
                        className={`hero-tab ${activeTab === 'projects' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('projects');
                          restartTimer();
                        }}
                        aria-selected={activeTab === 'projects'}
                        aria-controls="hero-panel-projects"
                      >
                        Projects
                      </button>
                    </div>
                  ) : (
                    <span className="hero-panel-eyebrow">Community updates</span>
                  )}
                </div>

                <div className="hero-tabpanels">
                  {/* Notices */}
                  <div
                    id="hero-panel-notices"
                    className={`hero-tabpanel ${activeTab === 'notices' ? 'is-active' : ''}`}
                    role="tabpanel"
                    aria-labelledby="hero-tab-notices"
                    hidden={activeTab !== 'notices'}
                  >
                    <div className="current-items-overview">
                      {isLoading ? (
                        <HeroPanelSkeleton />
                      ) : notices.length === 0 ? (
                        <HeroEmptyState
                          icon="bi-megaphone"
                          title="No notices right now"
                          message="Check back soon for community updates, or get in touch with us."
                          actionLabel="Contact us"
                          onAction={() => setShowContactModal(true)}
                        />
                      ) : currentNotice ? (
                        <>
                          <HeroMediaStage
                            src={getNoticeFileUrl(currentNotice.id)}
                            alt={currentNotice.title || 'Important Notice'}
                            fallbackIcon="bi-megaphone"
                            fallbackLabel="Notice"
                            openLabel={`Open notice: ${currentNotice.title || 'Important Notice'}`}
                            onOpen={() => openFullscreen(currentNoticeIndex)}
                            count={notices.length}
                            itemLabel="notice"
                            onPrev={prevNotice}
                            onNext={nextNotice}
                          />
                          <HeroSlideBar
                            title={currentNotice.title || 'Important Notice'}
                            meta={
                              formatPostedDate(currentNotice.createdAt)
                                ? `Posted ${formatPostedDate(currentNotice.createdAt)}`
                                : undefined
                            }
                            index={currentNoticeIndex}
                            count={notices.length}
                            itemLabel="notice"
                            onDot={goToSlide}
                            isCopied={copiedNoticeId === currentNotice.id}
                            onShare={() => void copyNoticeShareLink(currentNotice.id)}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>

                  {/* Events */}
                  {showEventsAndProjects && (
                    <div
                      id="hero-panel-events"
                      className={`hero-tabpanel ${activeTab === 'events' ? 'is-active' : ''}`}
                      role="tabpanel"
                      aria-labelledby="hero-tab-events"
                      hidden={activeTab !== 'events'}
                    >
                      {isLoadingEventsProjects ? (
                        <HeroPanelSkeleton />
                      ) : upcomingEvents.length === 0 ? (
                        <HeroEmptyState
                          icon="bi-calendar2-week"
                          title="No upcoming events this week"
                          message="Nothing scheduled right now, stay in the loop for the next gathering."
                          actionLabel="Get updates"
                          onAction={() => setShowContactModal(true)}
                        />
                      ) : currentUpcomingEvent ? (
                        <>
                          <HeroMediaStage
                            src={currentUpcomingEvent.imageId ? getImageUrlById(currentUpcomingEvent.imageId) : undefined}
                            alt={currentUpcomingEvent.name}
                            fallbackIcon="bi-calendar-event"
                            fallbackLabel="Event details"
                            openLabel={`View details for ${currentUpcomingEvent.name}`}
                            onOpen={() => setPreviewEvent(currentUpcomingEvent)}
                            count={upcomingEvents.length}
                            itemLabel="event"
                            onPrev={prevEvent}
                            onNext={nextEvent}
                          />
                          <HeroSlideBar
                            title={currentUpcomingEvent.name}
                            meta={
                              <>
                                {formatEventDate(currentUpcomingEvent.dtDateOfEvent)} ·{' '}
                                {formatEventTime(currentUpcomingEvent.dtTimeStartOfEvent)} –{' '}
                                {formatEventTime(currentUpcomingEvent.dtTimeEndOfEvent)}
                              </>
                            }
                            index={currentEventIndex}
                            count={upcomingEvents.length}
                            itemLabel="event"
                            onDot={goToEventSlide}
                            isCopied={copiedEventId === currentUpcomingEvent.id}
                            onShare={() => void copyEventShareLink(currentUpcomingEvent.id)}
                          />
                        </>
                      ) : null}
                    </div>
                  )}

                  {/* Projects */}
                  {showEventsAndProjects && (
                    <div
                      id="hero-panel-projects"
                      className={`hero-tabpanel ${activeTab === 'projects' ? 'is-active' : ''}`}
                      role="tabpanel"
                      aria-labelledby="hero-tab-projects"
                      hidden={activeTab !== 'projects'}
                    >
                      {isLoadingEventsProjects ? (
                        <HeroPanelSkeleton />
                      ) : activeProjects.length === 0 ? (
                        <HeroEmptyState
                          icon="bi-heart"
                          title="No active projects"
                          message="When a community project opens, you’ll see progress and ways to support it here."
                          actionLabel="Contact us"
                          onAction={() => setShowContactModal(true)}
                        />
                      ) : currentProject ? (
                        <>
                          <HeroMediaStage
                            src={currentProject.imageId ? getImageUrlById(currentProject.imageId) : undefined}
                            alt={currentProject.name}
                            fallbackIcon="bi-heart"
                            fallbackLabel="Community project"
                            openLabel={`View details for ${currentProject.name}`}
                            onOpen={() => setPreviewProject(currentProject)}
                            count={activeProjects.length}
                            itemLabel="project"
                            onPrev={prevProject}
                            onNext={nextProject}
                          />
                          <HeroSlideBar
                            title={currentProject.name}
                            meta={
                              <>
                                <strong className="hero-slidebar__highlight">
                                  {getProjectProgress(currentProject.fundsRaised, currentProject.fundsNeeded)}%
                                </strong>{' '}
                                · {formatCurrency(currentProject.fundsRaised)} of {formatCurrency(currentProject.fundsNeeded)} raised
                              </>
                            }
                            index={currentProjectIndex}
                            count={activeProjects.length}
                            itemLabel="project"
                            onDot={goToProjectSlide}
                            isCopied={copiedProjectId === currentProject.id}
                            onShare={() => void copyProjectShareLink(currentProject.id)}
                          >
                            <div
                              className="progress hero-project-progress mt-2 mb-0"
                              style={{ height: 6 }}
                              role="progressbar"
                              aria-label="Funds raised"
                              aria-valuenow={getProjectProgress(currentProject.fundsRaised, currentProject.fundsNeeded)}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              <div
                                className="progress-bar"
                                style={{
                                  width: `${getProjectProgress(currentProject.fundsRaised, currentProject.fundsNeeded)}%`,
                                }}
                              />
                            </div>
                          </HeroSlideBar>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Event detail lightbox */}
      {previewEvent && (
        <div
          className="detail-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Event details"
          onClick={() => setPreviewEvent(null)}
        >
          <div className="detail-lightbox__dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="detail-lightbox__close"
              onClick={() => setPreviewEvent(null)}
              aria-label="Close"
            >
              <i className="bi bi-x-lg" />
            </button>
            <div className="detail-lightbox__grid">
              <div className="detail-lightbox__media">
                {previewEvent.imageId ? (
                  <button
                    type="button"
                    className="detail-lightbox__media-frame border-0 p-0 bg-transparent"
                    onClick={() => {
                      setZoomedImage(getImageUrlById(previewEvent.imageId));
                      setZoomedImageTitle(previewEvent.name);
                    }}
                    aria-label="Zoom event image"
                  >
                    <img
                      src={getImageUrlById(previewEvent.imageId)}
                      alt=""
                      className="detail-lightbox__image"
                    />
                  </button>
                ) : (
                  <div className="detail-lightbox__media-fallback">
                    <i className="bi bi-calendar-event" aria-hidden />
                    <span>Event</span>
                  </div>
                )}
              </div>
              <div className="detail-lightbox__panel">
                <div className="detail-lightbox__panel-body">
                  <div className="detail-lightbox__meta">
                    <span className="detail-lightbox__badge detail-lightbox__badge--event">
                      <i className="bi bi-calendar-event" aria-hidden />
                      Community Event
                    </span>
                    <span className="detail-lightbox__date">
                      {formatEventDate(previewEvent.dtDateOfEvent)}
                    </span>
                  </div>
                  <h2 className="detail-lightbox__title">{previewEvent.name}</h2>
                  <div className="detail-lightbox__facts">
                    <span>
                      <i className="bi bi-clock" aria-hidden />
                      {formatEventTime(previewEvent.dtTimeStartOfEvent)} –{' '}
                      {formatEventTime(previewEvent.dtTimeEndOfEvent)}
                    </span>
                  </div>
                  {(previewEvent.descriptionHtml || previewEvent.description) ? (
                    <div
                      className="detail-lightbox__prose rich-text-content"
                      dangerouslySetInnerHTML={{
                        __html: previewEvent.descriptionHtml || previewEvent.description,
                      }}
                    />
                  ) : (
                    <p className="detail-lightbox__muted mb-0">No additional details yet.</p>
                  )}
                </div>
                <div className="detail-lightbox__actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => void copyEventShareLink(previewEvent.id)}
                  >
                    <i className={`bi ${copiedEventId === previewEvent.id ? 'bi-check2' : 'bi-share-fill'} me-2`} />
                    {copiedEventId === previewEvent.id ? 'Copied' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project detail lightbox */}
      {previewProject && (
        <div
          className="detail-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Project details"
          onClick={() => setPreviewProject(null)}
        >
          <div className="detail-lightbox__dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="detail-lightbox__close"
              onClick={() => setPreviewProject(null)}
              aria-label="Close"
            >
              <i className="bi bi-x-lg" />
            </button>
            <div className="detail-lightbox__grid">
              <div className="detail-lightbox__media">
                {previewProject.imageId ? (
                  <button
                    type="button"
                    className="detail-lightbox__media-frame border-0 p-0 bg-transparent"
                    onClick={() => {
                      setZoomedImage(getImageUrlById(previewProject.imageId));
                      setZoomedImageTitle(previewProject.name);
                    }}
                    aria-label="Zoom project image"
                  >
                    <img
                      src={getImageUrlById(previewProject.imageId)}
                      alt=""
                      className="detail-lightbox__image"
                    />
                  </button>
                ) : (
                  <div className="detail-lightbox__media-fallback">
                    <i className="bi bi-heart" aria-hidden />
                    <span>Project</span>
                  </div>
                )}
              </div>
              <div className="detail-lightbox__panel">
                <div className="detail-lightbox__panel-body">
                  <div className="detail-lightbox__meta">
                    <span className="detail-lightbox__badge detail-lightbox__badge--project">
                      <i className="bi bi-heart" aria-hidden />
                      Community Project
                    </span>
                    <span className="detail-lightbox__date">
                      {formatPostedDate(previewProject.dtCreated)
                        ? `Started ${formatPostedDate(previewProject.dtCreated)}`
                        : null}
                    </span>
                  </div>
                  <h2 className="detail-lightbox__title">{previewProject.name}</h2>
                  <div className="detail-lightbox__facts">
                    <span>
                      <i className="bi bi-cash-stack" aria-hidden />
                      {formatCurrency(previewProject.fundsRaised)} raised of{' '}
                      {formatCurrency(previewProject.fundsNeeded)} goal (
                      {getProjectProgress(previewProject.fundsRaised, previewProject.fundsNeeded)}%)
                    </span>
                  </div>
                  <div className="detail-lightbox__progress" aria-hidden>
                    <div
                      className="detail-lightbox__progress-bar"
                      style={{
                        width: `${getProjectProgress(previewProject.fundsRaised, previewProject.fundsNeeded)}%`,
                      }}
                    />
                  </div>
                  {(previewProject.descriptionHtml || previewProject.description) ? (
                    <div
                      className="detail-lightbox__prose rich-text-content"
                      dangerouslySetInnerHTML={{
                        __html: previewProject.descriptionHtml || previewProject.description,
                      }}
                    />
                  ) : (
                    <p className="detail-lightbox__muted mb-0">No additional details yet.</p>
                  )}
                </div>
                <div className="detail-lightbox__actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => void copyProjectShareLink(previewProject.id)}
                  >
                    <i className={`bi ${copiedProjectId === previewProject.id ? 'bi-check2' : 'bi-share-fill'} me-2`} />
                    {copiedProjectId === previewProject.id ? 'Copied' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Lightbox (shared) */}
      {zoomedImage && (
        <div
          className="modal show d-block image-zoom-lightbox"
          style={{ zIndex: 1060 }}
          onClick={() => setZoomedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image view"
        >
          <div className="image-zoom-lightbox__content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="image-zoom-lightbox__close"
              onClick={() => setZoomedImage(null)}
              aria-label="Close zoomed image"
            >
              <i className="bi bi-x-lg" />
            </button>
            <img src={zoomedImage} alt={zoomedImageTitle} className="image-zoomed-img" />
            {zoomedImageTitle && <p className="image-zoom-lightbox__title">{zoomedImageTitle}</p>}
          </div>
        </div>
      )}

      <ContactModal show={showContactModal} onHide={() => setShowContactModal(false)} />

      {/* Notice detail lightbox */}
      {isFullscreen && fullscreenNotice && (
        <div
          className="detail-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Notice details"
          onClick={closeFullscreen}
        >
          <div className="detail-lightbox__dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="detail-lightbox__close"
              onClick={closeFullscreen}
              aria-label="Close"
            >
              <i className="bi bi-x-lg" />
            </button>
            <div className="detail-lightbox__grid">
              <div className="detail-lightbox__media">
                {notices.length > 1 ? (
                  <>
                    <button
                      type="button"
                      className="detail-lightbox__media-nav detail-lightbox__media-nav--prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        goFullscreenPrev();
                      }}
                      aria-label="Previous notice"
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    <button
                      type="button"
                      className="detail-lightbox__media-nav detail-lightbox__media-nav--next"
                      onClick={(e) => {
                        e.stopPropagation();
                        goFullscreenNext();
                      }}
                      aria-label="Next notice"
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="detail-lightbox__media-frame border-0 p-0 bg-transparent"
                  onClick={() => {
                    setZoomedImage(getNoticeFileUrl(fullscreenNotice.id));
                    setZoomedImageTitle(fullscreenNotice.title || 'Important Notice');
                  }}
                  aria-label="Inspect notice image"
                >
                  <img
                    src={getNoticeFileUrl(fullscreenNotice.id)}
                    alt={fullscreenNotice.title || 'Important Notice'}
                    className="detail-lightbox__image"
                  />
                </button>
                {notices.length > 1 ? (
                  <div className="detail-lightbox__counter" aria-live="polite">
                    {fullscreenIndex + 1} / {notices.length}
                  </div>
                ) : null}
              </div>
              <div className="detail-lightbox__panel">
                <div className="detail-lightbox__panel-body">
                  <div className="detail-lightbox__meta">
                    <span className="detail-lightbox__badge">
                      <i className="bi bi-megaphone" aria-hidden />
                      Important Notice
                    </span>
                    {formatPostedDate(fullscreenNotice.createdAt) ? (
                      <span className="detail-lightbox__date">
                        Posted {formatPostedDate(fullscreenNotice.createdAt)}
                      </span>
                    ) : null}
                  </div>
                  <h2 className="detail-lightbox__title">
                    {fullscreenNotice.title || 'Important Notice'}
                  </h2>
                  {fullscreenNotice.descriptionHtml ? (
                    <div
                      className="detail-lightbox__prose rich-text-content"
                      dangerouslySetInnerHTML={{ __html: fullscreenNotice.descriptionHtml }}
                    />
                  ) : fullscreenNotice.description ? (
                    <p className="detail-lightbox__prose mb-0">{fullscreenNotice.description}</p>
                  ) : (
                    <p className="detail-lightbox__muted mb-0">No description available.</p>
                  )}
                </div>
                {notices.length > 1 ? (
                  <div className="detail-lightbox__dots">
                    {notices.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`detail-lightbox__dot ${index === fullscreenIndex ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFullscreenIndex(index);
                        }}
                        aria-label={`Go to notice ${index + 1}`}
                      />
                    ))}
                  </div>
                ) : null}
                <div className="detail-lightbox__actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      void copyNoticeShareLink(fullscreenNotice.id);
                    }}
                  >
                    <i className={`bi ${copiedNoticeId === fullscreenNotice.id ? 'bi-check2' : 'bi-share-fill'} me-2`} />
                    {copiedNoticeId === fullscreenNotice.id ? 'Copied' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
